/**
 * Consent gate component.
 *
 * Wraps protected routes. If the authenticated user has no consent record
 * or an outdated policy version, they are redirected to /consent.
 * Renders a full-page error state if the consent API fails.
 *
 * Must be used inside ProtectedRoute (auth check runs first).
 *
 * @module ConsentGate
 */

import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useConsentStatus } from "@/hooks/useConsentStatus";
import { ROUTES } from "@/lib/routeConstants";

interface ConsentGateProps {
  readonly children: React.ReactNode;
}

export default function ConsentGate({ children }: ConsentGateProps) {
  const { t } = useTranslation();
  const { isLoading, isError, needsConsent, refetch } = useConsentStatus();

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            {t("consent.gate.loadingTitle", {
              defaultValue: "Checking your privacy settings…",
            })}
          </p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4">
        <div className="text-center max-w-sm space-y-4">
          <h2 className="text-xl font-semibold">
            {t("consent.gate.errorTitle", {
              defaultValue: "Unable to load privacy settings",
            })}
          </h2>
          <p className="text-muted-foreground">
            {t("consent.gate.errorMessage", {
              defaultValue:
                "We could not load your privacy preferences. Please check your connection and try again.",
            })}
          </p>
          <Button onClick={() => refetch()}>
            {t("consent.gate.retryButton", { defaultValue: "Retry" })}
          </Button>
        </div>
      </div>
    );
  }

  if (needsConsent) {
    return <Navigate to={ROUTES.CONSENT} replace />;
  }

  return <>{children}</>;
}
